import { Host, h } from "@stencil/core";
import { SEGMENTED_CONTROL_PARTS_DICTIONARY } from "../../../../common/reserved-names";
/**
 * Segmented control is used to pick one choice from a linear set of closely related choices, and immediately apply that selection.
 * This control represents and item of the ch-segmented-control-render
 *
 * @part selected - ...
 */
export class ChSegmentedControlItem {
    constructor() {
        this.accessibleName = undefined;
        this.between = false;
        this.caption = undefined;
        this.disabled = false;
        this.endImgSrc = undefined;
        this.endImgType = "background";
        this.first = false;
        this.last = false;
        this.selected = undefined;
        this.startImgSrc = undefined;
        this.startImgType = "background";
    }
    el;
    /**
     * Fired when the control is selected by user interaction.
     */
    selectedChange;
    #parts = () => `action${this.disabled ? ` ${SEGMENTED_CONTROL_PARTS_DICTIONARY.DISABLED}` : ""} ${this.selected
        ? SEGMENTED_CONTROL_PARTS_DICTIONARY.SELECTED
        : SEGMENTED_CONTROL_PARTS_DICTIONARY.UNSELECTED}${this.first ? ` ${SEGMENTED_CONTROL_PARTS_DICTIONARY.FIRST}` : ""}${this.last ? ` ${SEGMENTED_CONTROL_PARTS_DICTIONARY.LAST}` : ""}${this.between ? ` ${SEGMENTED_CONTROL_PARTS_DICTIONARY.BETWEEN}` : ""}`;
    #handleSelectedChange = (event) => {
        event.stopPropagation();
        this.selectedChange.emit(this.el.id);
    };
    render() {
        const hasStartImg = !!this.startImgSrc;
        const hasEndImg = !!this.endImgSrc;
        const hasImages = hasStartImg || hasEndImg;
        return (h(Host, { key: '022b99c7a41d09758589a93a836fd62c13bf5061', role: "listitem" }, h("button", { key: '9555c6f484e2b3711034b36330248f78a747abb7', "aria-label": this.accessibleName || null, "aria-selected": this.selected ? "true" : null, class: hasImages
                ? {
                    [`start-img-type--${this.startImgType ?? "background"} img--start`]: hasStartImg,
                    [`end-img-type--${this.endImgType ?? "background"} img--end`]: hasEndImg
                }
                : undefined, part: this.#parts(), style: hasImages
                ? {
                    "--ch-start-img": hasStartImg
                        ? `url("${this.startImgSrc}")`
                        : undefined,
                    "--ch-end-img": hasEndImg
                        ? `url("${this.endImgSrc}")`
                        : undefined
                }
                : undefined, type: "button", onClick: !this.selected ? this.#handleSelectedChange : null }, this.caption)));
    }
    static get is() { return "ch-segmented-control-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["segmented-control-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["segmented-control-item.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "between": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control is the not the first or last item in the\nch-segmented-control-render."
                },
                "attribute": "between",
                "reflect": false,
                "defaultValue": "false"
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the caption that the control will display."
                },
                "attribute": "caption",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "endImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the end image."
                },
                "attribute": "end-img-src",
                "reflect": false
            },
            "endImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the end image will be rendered."
                },
                "attribute": "end-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "first": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control is the first item in the ch-segmented-control-render."
                },
                "attribute": "first",
                "reflect": false,
                "defaultValue": "false"
            },
            "last": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control is the last item in the ch-segmented-control-render."
                },
                "attribute": "last",
                "reflect": false,
                "defaultValue": "false"
            },
            "selected": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the control is selected."
                },
                "attribute": "selected",
                "reflect": false
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the start image."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the start image will be rendered."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            }
        };
    }
    static get events() {
        return [{
                "method": "selectedChange",
                "name": "selectedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the control is selected by user interaction."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
}
